import './NodePackagingLevel.css';

import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { ReactComponent as Path } from '../../assets/icons/path.svg';
import { Badge } from '../core/Badge';
import { Icon } from '../core/Icon';

interface NodePackagingLevelProps {
    icon?: any;
    title: string;
    titleTwo: string;
    isCompliant?: boolean;
    hasWarnings?: boolean;
    level?: number;
    isDisabled?: boolean;
    badge?: string;
    nextLowerQuantity?: number;
    isSelected?: boolean;
    onClick?: () => void;
    isLastItem?: boolean;
    isFirstItem?: boolean;
    withoutPath?: boolean;
}

const NodePackagingLevel = React.forwardRef<HTMLButtonElement, NodePackagingLevelProps>(({
    icon = 'base',
    title,
    titleTwo,
    level = 0,
    isDisabled,
    badge,
    nextLowerQuantity,
    isCompliant,
    hasWarnings,
    isSelected,
    onClick,
    isLastItem,
    isFirstItem = false,
    withoutPath = false,
    ...props
}, ref) => {
    const { t } = useTranslation();
    const borderColor = !withoutPath ? '!tw-border-primary-active ' : '!tw-border-primary';
    const levelBorderClass =
    isFirstItem
        ? 'tw-border !tw-border-primary-active tw-bg-fill-primary ' + borderColor
        : isLastItem
            ? '!tw-border-b-0 !tw-border-transparent'
            : 'tw-border tw-border-primary';

    const stateClasses = {
        // Interactive states
        'hover:tw-bg-fill-primary-hover': !isSelected && !isDisabled,
        'active:tw-bg-fill-primary-press': !isSelected && !isDisabled,

        // Selection state
        '!tw-bg-fill-secondary': isSelected,

        // Focus states
        'focus-visible:!tw-border-2': !isSelected,
        'focus-visible:!tw-border-primary-focus': !isSelected,
        'focus-visible:!tw-rounded-sm': !isSelected,
        'focus-visible:tw-outline-none': !isSelected,
        'focus-visible:!tw-bg-fill-primary-focus': !isSelected,
        'focus-visible:tw-mt-[-1px]': !isSelected,

        // Disabled states
        'tw-cursor-default': isDisabled || isSelected,
        'tw-border tw-border-primary': isDisabled,
    };

    const getTextClasses = (isDisabled?: boolean, isFirstItem?: boolean, className?: string) =>
        cn(
            className,
            'font-dynamic lg-font-dynamic',
            '!tw-overflow-hidden !tw-text-ellipsis !tw-whitespace-nowrap tw-m-0',
            isDisabled ? '!tw-text-t-inactive': '!tw-text-t-primary',
            (isFirstItem && !withoutPath)
                ? '!tw-font-bold lg:!tw-font-bold'
                : '',
        );

    const defaulPadding = level === 1 && 'tw-pl-2';

    const ValidationIcon = ({
        hasWarnings,
        isCompliant,
    }: {
        hasWarnings?: boolean;
        isCompliant?: boolean;
    }) => {
        const conditions = [
            {
                show: hasWarnings,
                icon: ExclamationCircleIcon,
                variant: 'warning',
                key: 'warning',
            },
            {
                show: isCompliant === true,
                icon: CheckCircleIcon,
                variant: 'success',
                key: 'success',
            },
            {
                show: isCompliant === false,
                icon: XCircleIcon,
                variant: 'destructive',
                key: 'destructive',
            },
        ];

        const iconsToRender = conditions
            .filter((item) => item.show)
            .map(({ icon, variant, key }) => (
                <Icon
                    key={key}
                    icon={icon}
                    variant={variant as any}
                    size="sm"
                    className="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6"
                />
            ));

        if (iconsToRender.length === 0) return null;

        return <>{iconsToRender}</>;
    };
    const borderRadiusClass = isFirstItem ? 'tw-rounded-t is-first-item' : isLastItem ? 'tw-rounded-b is-last-item' : '';
    return (
        <button
            type="button"
            ref={ref}
            onClick={() => onClick?.()}
            className={cn(
                'node-packaging-level',
                'tw-border-solid tw-bg-fill-primary !tw-border-l-0 !tw-border-r-0 !tw-border-t-0 tw-w-[480px] lg:tw-w-[536px]',
                'tw-duration-100 tw-ease-out',
                'tw-pr-2 tw-pt-2 tw-pb-2',
                '!tw-shadow-none',
                levelBorderClass,
                defaulPadding,
                stateClasses,
                borderRadiusClass
            )}
            style={{
                paddingLeft:`calc(8px + ${(level - 1) * 12}px)`,
            }}
            disabled={isDisabled}
            {...props}
        >
            <div className="tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <div className="tw-flex tw-items-center tw-gap-1">
                        {(level !== 1 && !withoutPath) && (
                            <Icon
                                icon={Path}
                                size="sm"
                                variant={isDisabled ? 'lighter-gray' : 'dark'}
                                className={`${isDisabled ? '!tw-icon-inactive' : 'tw-text-t-primary'} !tw-w-auto !tw-h-2 tw-mt-[3px] tw-mb-[9px] lg-mt-dynamic lg-mb-dynamic`}
                            />
                        )}
                        <Icon
                            icon={icon}
                            size="sm"
                            variant={isDisabled ? 'lighter-gray' : 'dark'}
                            className={`${isDisabled ? '!tw-icon-inactive' : 'tw-text-t-primary'} tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6 `}
                        />
                        <p className={getTextClasses(isDisabled, isFirstItem, titleTwo && 'max-width-dynamic lg-max-width-dynamic')} title={title}>{title}</p>
                        <p className={getTextClasses(isDisabled, isFirstItem)} title={titleTwo}>{titleTwo}</p>
                    </div>
                    {nextLowerQuantity !== undefined && (
                        <Badge variant="neutral" number={nextLowerQuantity}>
                            {t('PIECES_ABBREVIATION')}
                        </Badge>
                    )}
                    {badge && <Badge>{t(badge)}</Badge>}
                </div>
                {/* Validation State Icons aligned to end */}
                <div className="tw-flex tw-pl-2 tw-justify-end tw-items-center">
                    <ValidationIcon
                        hasWarnings={hasWarnings}
                        isCompliant={isCompliant}
                    />
                </div>
            </div>
        </button>
    );
});

NodePackagingLevel.displayName = 'NodePackagingLevel';

export  { NodePackagingLevel, type NodePackagingLevelProps };
